import { landmineNames } from '@mm/constants/landmine-names';
import { SEEN_SEND_LIMIT_NOTIFICATION_TABLE_KEY } from '@mm/constants/message-send-limits';
import { registerFullStoryUser } from '@mm/plugins/FullStory';
import { useAdConfigCRUDActions } from '@mm/state/actions/AdConfigCRUDActions';
import { useCampaignsOverviewCRUDActions } from '@mm/state/actions/CampaignsOverviewCRUDActions';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import { useChatStartersCRUDActions } from '@mm/state/actions/ChatStartersCRUDActinos';
import { useEmailAccountCRUDActions } from '@mm/state/actions/EmailAccountCRUDActions';
import { useFeatureFlagsCRUDActions } from '@mm/state/actions/FeatureFlagsCRUDActions';
import { useInstagramCRUDActions } from '@mm/state/actions/InstagramCRUDActions';
import { useMessengerAdCRUDActions } from '@mm/state/actions/MessengerAdCRUDActions';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import { useTwilioCRUDActions } from '@mm/state/actions/TwilioCRUDActions';
import { getPlanInfo } from '@mm/state/helpers/billing-info-helpers';
import JSONUtils from '@mm/state/helpers/json-utils';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  activeChatbotIdSelector,
  selectIsShopifyContext,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { LocalStorageService } from '@mm/utils';
import { get } from 'lodash';
import moment from 'moment';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { matchPath, Redirect } from 'react-router';
import Spinner from '../../../../common/components/Spinner';
import { PAGES_UPGRADE_COUNT } from '../../../../common/constants/payments';
import { CONNECT_PAGE, SIGNOUT } from '../../../../common/constants/routes';
import { useDesktopNotifications } from '../../../../common/hooks/use-desktop-notifications/use-desktop-notifications';
import { UserRoleContainer } from '../../../../common/hooks/use-route-guard';
import { useFreeTrialUpgradeModal } from '../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { SendLimitLookupTable } from '../../../../common/modals/MonthlySendsWarningModal/interfaces';
import { BotBuilderRoutes } from '../../../../routes/bot-builder-routes';
import { Routes } from '../../../../routes/top-level-routes';
import { ChatbotEditorPageProps } from '../ChatbotEditorPage';
import { getActiveChatbotId } from './helpers';

type Props = {
  chatbotEditorPageProps: ChatbotEditorPageProps;
  children: ReactNode;
};

const FULL_STORY_RECORD_START_DATE = '2023-12-10T00:00:00.000Z';

const useUpgradeTeamModal = (props: ChatbotEditorPageProps) => {
  const {
    activeCompany,
    safeTimeout,
    updateUserAttributes,
    session,
    modalOpen,
    justCompletedOnboarding,
    availablePages,
    featureFlags,
  } = props;

  const modalActions = useModalActions();

  const needsUpgrade =
    activeCompany?.editable &&
    activeCompany.freePricePlan &&
    availablePages?.data.length > PAGES_UPGRADE_COUNT &&
    !featureFlags.connectPageUnlimitedPages;

  const showUpgradeTeamModal =
    needsUpgrade &&
    !session.userAttributes.seenUpgradeTeamPromo &&
    !modalOpen &&
    !justCompletedOnboarding;

  const upgradeToTeamModalCallback = useCallback(() => {
    if (!modalOpen) {
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['PROMOTIONS__TEAM-UPGRADE'],
        feature: 'connectPageUnlimitedPages',
      });

      if (activeCompany) {
        updateUserAttributes(activeCompany.id, session.id, {
          seenUpgradeTeamPromo: true,
        });
      }
    }
  }, [modalOpen, updateUserAttributes, activeCompany, session]);

  useEffect(() => {
    if (showUpgradeTeamModal) {
      safeTimeout(upgradeToTeamModalCallback, 2500);
    }
  }, [showUpgradeTeamModal, modalOpen]);
};

const usePageMessageUtilizationModal = (props: ChatbotEditorPageProps) => {
  const {
    activeMessagesStatistic,
    openMonthlySendsWarningModal,
    activeFacebookPage,
    safeTimeout,
    activeCompany,
  } = props;
  const planInfo = getPlanInfo(activeFacebookPage);
  const isOwnerOrAdmin = activeCompany && activeCompany.editable;

  const { isFreeTrialPromotionWithFutureSubscription } =
    useFreeTrialUpgradeModal(activeFacebookPage);

  const canShowModal =
    !(planInfo.isCustomTeam || planInfo.isLegacyTeamPlan) &&
    isOwnerOrAdmin &&
    activeFacebookPage &&
    (!activeFacebookPage.softLimitReached ||
      !activeFacebookPage.hardLimitReached) &&
    !isFreeTrialPromotionWithFutureSubscription;

  useEffect(() => {
    if (
      canShowModal &&
      activeFacebookPage &&
      !activeFacebookPage.overagesEnabled
    ) {
      const pageId = activeFacebookPage ? activeFacebookPage.id : '';

      if (activeMessagesStatistic.utilizationPercentage > 99) {
        const sendLimitLookupTable = LocalStorageService.getItem(
          SEEN_SEND_LIMIT_NOTIFICATION_TABLE_KEY,
        );
        const parsedLookupTable =
          JSONUtils.safeParse<SendLimitLookupTable>(sendLimitLookupTable) || {};

        if (
          !parsedLookupTable.oneHundredPercent ||
          !parsedLookupTable.oneHundredPercent[pageId]
        ) {
          safeTimeout(openMonthlySendsWarningModal, 500);
        }
      } else if (activeMessagesStatistic.utilizationPercentage > 79) {
        const sendLimitLookupTable = LocalStorageService.getItem(
          SEEN_SEND_LIMIT_NOTIFICATION_TABLE_KEY,
        );
        const parsedLookupTable =
          JSONUtils.safeParse<SendLimitLookupTable>(sendLimitLookupTable) || {};

        if (
          !parsedLookupTable.eightyToNinetyNinePercent ||
          !parsedLookupTable.eightyToNinetyNinePercent[pageId]
        ) {
          safeTimeout(openMonthlySendsWarningModal, 500);
        }
      }
    }
  }, [canShowModal, activeMessagesStatistic]);
};

const isDashboard = (path: string) => {
  return [
    BotBuilderRoutes.dashboard,
    BotBuilderRoutes.index,
    BotBuilderRoutes.home,
    BotBuilderRoutes.settingsMaintenance,
    Routes.chatbotEditor,
  ].some(route =>
    matchPath(path, {
      path: route.path,
      exact: true,
      strict: false,
    }),
  );
};

// Only creating this component as a way to use hooks but also avoid merge conflicts
// as ChatbotEditorPage is heavily edited. Combine both components once undashboard
// is in master.
const FetchBotData = (props: Props) => {
  const {
    getAttributes,
    retrieveBusinessAttributes,
    retrieveChatbot,
    retrieveFacebookPage,
    getChatbotList,
    location,
    inviteToken,
    getPageGroups,
    getDropDownPages,
    getPages,
    activateChatbotEditor,
    getAudiences,
    companyId,
    resetToken,
    history,
    activateCompany,
    session,
    activeCompany,
    mmBuyBeforeTrying,
    activeFacebookPage,
    getFacebookPageList,
    getTags,
    getUsers,
    justCompletedOnboarding,
    modalOpen,
    openPrettyErrorModal,
  } = props.chatbotEditorPageProps;
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const userCountry = useSelector((store: StoreState) => store.env.userCountry);
  const [fetchComplete, setFetchComplete] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [currentCompanyHasBots, setCurrentCompanyHasBots] = useState(false);
  const twilioCRUDActions = useTwilioCRUDActions();
  const pathBotId = Number(location.pathname.split('/')[2]); // TODO fixme, use router match props or imperative match on route constants
  useUpgradeTeamModal(props.chatbotEditorPageProps);
  usePageMessageUtilizationModal(props.chatbotEditorPageProps);
  const {
    isLoading,
    roleFlags: { isLiveChatAgent, isFacebookPageCollaborator, isCollaborator },
  } = UserRoleContainer.useContainer();

  const campaignsOverviewCRUDActions = useCampaignsOverviewCRUDActions();
  const chatStartersCRUDActions = useChatStartersCRUDActions();
  const featureFlagsCRUDActions = useFeatureFlagsCRUDActions();
  const instagramCRUDActions = useInstagramCRUDActions();
  const promoterCRUDActions = usePromoterCRUDActions();
  const adConfigCRUDActions = useAdConfigCRUDActions();
  const messengerAdCRUDActions = useMessengerAdCRUDActions();
  const chatbotCRUDActions = useChatbotCRUDActions();
  const { openDesktopNotificationsModal } = useDesktopNotifications();
  const emailAccountCRUDActions = useEmailAccountCRUDActions();
  const { getAccountData } = useSubscriptionCRUDActions();

  const fetchBotData = async () => {
    if (companyId) {
      setFetching(true);
      const currentCompanyBotsList = await getChatbotList(companyId);
      setCurrentCompanyHasBots(
        !!currentCompanyBotsList && !!currentCompanyBotsList.length,
      );

      if (currentCompanyBotsList && currentCompanyBotsList.length) {
        const [chatbotId, chatbot] = await getActiveChatbotId(
          currentCompanyBotsList,
          pathBotId,
          session.userAttributes.activeChatbotId,
          async () => getFacebookPageList(companyId),
          retrieveChatbot,
          async (companyId: number) => activateCompany(session.id, companyId),
          openPrettyErrorModal,
          history,
        );

        if (chatbotId === activeChatbotId) {
          setFetching(false);
          setFetchComplete(true);
          return;
        }

        const activeChatbot = chatbot || (await retrieveChatbot(chatbotId));

        const activeFacebookPageId: number | undefined = get(
          activeChatbot,
          'connectedPages[0].id',
        );

        const twilioAccessible: number | undefined = get(
          activeChatbot,
          'connectedPages[0].twilioAccessible',
        );

        const activeFacebookPagePromise = activeFacebookPageId
          ? retrieveFacebookPage(activeFacebookPageId)
          : undefined;

        // TODO rethink after routes re composition
        if (isLiveChatAgent) {
          await activeFacebookPagePromise;
          activateChatbotEditor(chatbotId, session.id);
          getUsers(companyId);
        }

        if (!isLoading && !isLiveChatAgent) {
          // wait for this separately since it resets some redux state after completion
          await activeFacebookPagePromise;

          await Promise.all([
            ...(activeFacebookPageId
              ? [
                  getAttributes(activeFacebookPageId),
                  retrieveBusinessAttributes(activeFacebookPageId),
                  getTags(activeFacebookPageId),
                  featureFlagsCRUDActions.retrieveSubscriptionFeatures(
                    activeFacebookPageId,
                  ),
                  featureFlagsCRUDActions.retrieveFeaturesForPlans(
                    activeFacebookPageId,
                  ),
                  twilioAccessible &&
                    twilioCRUDActions.getAll(activeFacebookPageId, true),
                  instagramCRUDActions.getIGAccounts(activeFacebookPageId),
                  emailAccountCRUDActions.getAccounts(activeFacebookPageId),
                ]
              : []),
            getAccountData(companyId),
            getPageGroups(chatbotId),
            getDropDownPages(chatbotId),
            getAudiences(chatbotId),
            getUsers(companyId),
            chatStartersCRUDActions.retrieveAll({
              botId: chatbotId,
              kind: 'all',
            }),
            campaignsOverviewCRUDActions.retrieveAll({
              botId: chatbotId,
              kind: 'automation',
            }),

            // BEGIN: For Live-Chat/Inbox
            ...['customer_chat', 'fb_comment_guard'].map(kind =>
              promoterCRUDActions.retrieveAll(chatbotId, kind),
            ),
            adConfigCRUDActions.retrieveAll(chatbotId),
            messengerAdCRUDActions.retrieveAll(chatbotId),
            // END: For Live-Chat/Inbox
            promoterCRUDActions.retrieveAll(chatbotId, 'getemails'),
          ]);

          // Things to load in the background
          if (activeFacebookPageId) {
            chatbotCRUDActions.retrieveAll(activeFacebookPageId, true);
          }

          // TODO: Remove this action and 'state.view.chatbotEditor.activeChatbotId'
          // The entity 'activeChatbot' was added to chatbot module for partners feature and removed
          // the need for this. About half of our code uses activeChatbot and the other half uses activeChatbotId.
          // No need for 2 sources of truth.
          activateChatbotEditor(chatbotId, session.id);
        }
      } else if (isLiveChatAgent) {
        openPrettyErrorModal({
          title: 'Permission Error',
          errorMessage:
            'You don’t have access to any of the bots / pages. Ask company owner to provide access.',
          onClose: () => history.push(SIGNOUT),
        });
      }

      setFetchComplete(true);
      setFetching(false);
    }
  };

  const isShopify = useSelector(selectIsShopifyContext);

  const showDesktopNotificationsModal = useMemo(() => {
    return (
      fetchComplete &&
      !justCompletedOnboarding &&
      !modalOpen &&
      !isShopify &&
      !session.userAttributes.desktopNotifications
        ?.seenDesktopNotificationsPromoPopup
    );
  }, [
    fetchComplete,
    session.userAttributes,
    justCompletedOnboarding,
    modalOpen,
  ]);

  useEffect(() => {
    if (showDesktopNotificationsModal) {
      openDesktopNotificationsModal();
    }
  }, [fetchComplete, activeCompany, session, showDesktopNotificationsModal]);

  useEffect(() => {
    if (fetchComplete) {
      setFetchComplete(false);
    }

    if (!fetching || (fetching && fetchComplete)) fetchBotData();
  }, [pathBotId, isLoading, isLiveChatAgent]);

  if (fetchComplete) {
    if (currentCompanyHasBots) {
      if (
        (session?.sessionsCount || 0) < 3 &&
        !(session.admin || session.csAdmin) &&
        userCountry === 'US' &&
        moment(session.createdAt).isAfter(moment(FULL_STORY_RECORD_START_DATE))
      ) {
        registerFullStoryUser(
          session.id,
          `${session.firstName} ${session.lastName}`,
          session.email,
        );
      }
      return <div>{props.children}</div>;
    }

    // Redirect to connect page page as new layout only works with bots :)
    return isLiveChatAgent ? null : <Redirect to={CONNECT_PAGE} />;
  }

  // Don't show content until fetch is complete (would show cached data)
  return <Spinner show={true} />;
};

export default FetchBotData;
